import * as React from 'react';
import { cn } from '../../utils/cn';
import { Avatar, AvatarImage, AvatarFallback } from '../avatar';
import { avatarVariants } from '../avatar/avatar';
import { type VariantProps } from 'class-variance-authority';

/**
 * Avatar data for AvatarCircles component.
 * Can represent either a user avatar (with imageUrl/name) or an icon avatar.
 */
interface Avatar {
    /** URL to the avatar image */
    imageUrl?: string;
    /** Name of the person (used for initials fallback) */
    name?: string;
    /** Optional icon element to render instead of image (for special avatars like "Future team" or "Link access") */
    icon?: React.ReactNode;
}

/**
 * AvatarVariantProps is the type for the avatar props using the actual props from the component
 */
type AvatarVariantProps = VariantProps<typeof avatarVariants>;

/**
 * AvatarCirclesProps is the type for the avatar circles props
 */
export interface AvatarCirclesProps {
    className?: string;
    avatarClassName?: string;
    avatarUrls: Avatar[];
    size?: AvatarVariantProps['size'];
    border?: AvatarVariantProps['border'];
    customBorderColor?: string;
    status?: AvatarVariantProps['status'];
    maxAvatars?: number;
    /** When true, shows all avatars regardless of maxAvatars */
    showAll?: boolean;
    /** Enable overlapping avatars with negative spacing */
    overlap?: boolean;
}

export const AvatarCircles = ({
    className,
    avatarClassName,
    avatarUrls,
    size = 'square40',
    border = 'light',
    customBorderColor,
    status = 'none',
    maxAvatars = 3,
    showAll = false,
    overlap = true,
}: AvatarCirclesProps) => {
    const avatarCount = avatarUrls.length;
    // If showAll is true, show all avatars; otherwise use maxAvatars
    const avatarsToShow = showAll
        ? avatarUrls
        : avatarUrls.slice(0, maxAvatars);
    const remainingAvatars = showAll
        ? 0
        : Math.max(0, avatarCount - maxAvatars);

    return (
        <div className={cn('flex items-center gap-2', className)}>
            <div
                className={cn(
                    'z-10 flex rtl:space-x-reverse',
                    overlap ? '-space-x-3' : 'gap-1'
                )}
            >
                {avatarsToShow.map((avatar, index) => (
                    <Avatar
                        key={
                            avatar.name || avatar.imageUrl || `avatar-${index}`
                        }
                        className={avatarClassName}
                        size={size}
                        border={border}
                        customBorderColor={customBorderColor}
                        status={status}
                    >
                        {avatar.icon ? (
                            // Render icon-based avatar
                            <AvatarFallback
                                size={mapFallBackTextSize(size)}
                                className="bg-gray-200"
                            >
                                {avatar.icon}
                            </AvatarFallback>
                        ) : (
                            // Render image-based avatar
                            <>
                                <AvatarImage
                                    src={avatar.imageUrl}
                                    alt={avatar.name || `Avatar ${index + 1}`}
                                    fit="cover"
                                />
                                <AvatarFallback
                                    size={mapFallBackTextSize(size)}
                                >
                                    {avatar.name
                                        ? getInitials(avatar.name)
                                        : `${index + 1}`}
                                </AvatarFallback>
                            </>
                        )}
                    </Avatar>
                ))}
                {remainingAvatars > 0 && (
                    <Avatar
                        className={avatarClassName}
                        size={size}
                        border={border}
                        customBorderColor={customBorderColor}
                        status="none"
                    >
                        <AvatarFallback size={mapFallBackTextSize(size)}>
                            +{remainingAvatars}
                        </AvatarFallback>
                    </Avatar>
                )}
            </div>
        </div>
    );
};

// Helper function to get initials from a name
const getInitials = (name: string): string => {
    return name
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const mapFallBackTextSize = (
    size: AvatarVariantProps['size']
): 'xs' | 'sm' | 'md' | 'default' => {
    switch (size) {
        case 'square20':
            return 'xs';
        case 'square24':
            return 'xs';
        case 'square28':
            return 'sm';
        case 'square32':
            return 'md';
        case 'square40':
            return 'default';
        case 'square56':
            return 'default';
        case 'square64':
            return 'default';
        case 'square72':
            return 'default';
        default:
            return 'default';
    }
};
